import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';

const VideoEditModal = ({ isOpen, onClose, video, onSaveSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen && video) {
      setTitle(video.title || '');
      setDescription(video.description || '');
      setErrorMessage('');
    }
  }, [isOpen, video]);

  const handleSave = async () => {
    if (!title.trim()) {
      setErrorMessage('Title is required.');
      return;
    }

    setIsSaving(true);
    setErrorMessage('');

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('beatseek_token');

      const response = await fetch(`${API}/api/promo-videos/${video.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update video');
      }

      const updatedVideo = await response.json();
      onSaveSuccess(updatedVideo);
      onClose();
    } catch (error) {
      console.error('Error updating video:', error);
      setErrorMessage(error.message || 'Failed to update video. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !video) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-cyan-500/30 w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white">Edit Video</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            disabled={isSaving}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Title Input */}
          <div>
            <label className="block text-white font-medium mb-2">
              Video Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter video title"
              maxLength={100}
              className="w-full bg-slate-700 text-white border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400 transition-colors"
              disabled={isSaving}
            />
            <div className="text-gray-400 text-xs mt-1 text-right">
              {title.length}/100
            </div>
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-white font-medium mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your video..."
              rows={4}
              maxLength={500}
              className="w-full bg-slate-700 text-white border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
              disabled={isSaving}
            />
            <div className="text-gray-400 text-xs mt-1 text-right">
              {description.length}/500
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
              <p className="text-red-200 text-sm">{errorMessage}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-300 hover:text-white transition-colors font-medium"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !title.trim()}
            className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              !isSaving && title.trim()
                ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save size={16} />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoEditModal;
